/**
 * The browser leg of the workspace terminal (Phase 2 U2.2, fs#2337).
 *
 * Wire framing, both directions (src/flowstack/server/routes/workspace_tty.py):
 * binary frames carry raw PTY bytes; JSON text frames carry control —
 * `{type: "resize", cols, rows}` and `{type: "ping"}`, answered `{type: "pong"}`.
 * The first frame after the upgrade must be `{token}`, the USER token from
 * `GET …/workspace`, within 5 seconds.
 *
 * Close codes decide what happens next:
 *
 * - 4409 superseded — another tab took the session; wait for the user.
 * - 4404 not live / 4403 revoked — the session is over for this user.
 * - 4401 auth — the token was stale or invalid: one retry with a fresh one.
 * - anything else (4502 agent absent, 1013 too slow, a dropped socket) —
 *   reconnect with backoff, a fresh token each time.
 *
 * Retries are bounded (fs#2587): a refused pass or ticket, a failed status
 * read and every close above that retries count as one failed dial, and after
 * `maxAttempts` in a row with no attach the connection stops in `unreachable`
 * — the caller offers a restart — rather than "reconnecting" forever. An
 * attach (the first frame) resets the count; so does `connect()`.
 *
 * Every (re)attach makes the agent replay its scrollback ring, so the caller
 * clears its screen on `open` rather than appending a second copy.
 *
 * A separate terminal host (C2.9 (3), fs#2462): the session cookie is host-only
 * on the API host and never reaches it, so before EVERY connect and reconnect
 * the connection mints a one-minute ticket on the API host (through
 * `apiFetch`, under the session cookie) and redeems it at the terminal host
 * for a five-minute pass cookie ({@link redeemTtyPass}). The ticket travels in
 * JSON bodies only. Without a `ttyEndpoint` it does neither.
 */

import { apiFetch } from "@/api/client";
import { workspacePath } from "@/api/workspace";

export const CLOSE_AUTH = 4401;
export const CLOSE_REVOKED = 4403;
export const CLOSE_NOT_LIVE = 4404;
export const CLOSE_SUPERSEDED = 4409;
export const CLOSE_AGENT_ABSENT = 4502;

export type ConnectionState =
  | { kind: "connecting" }
  | { kind: "open" }
  | { kind: "reconnecting"; attempt: number }
  | { kind: "superseded" }
  | { kind: "ended"; code: number }
  | { kind: "denied" }
  | { kind: "unreachable" };

/** The slice of `WebSocket` this class uses, so tests can hand it a fake. */
export interface SocketLike {
  binaryType: BinaryType;
  readonly readyState: number;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  send(data: string | ArrayBufferLike | ArrayBufferView): void;
  close(code?: number): void;
}

export interface TerminalConnectionOptions {
  url: () => Promise<string>;
  /** A fresh USER token, or `null` when the session is no longer live. */
  token: () => Promise<string | null>;
  /** The allowed terminal host's origin, or `null` when the terminal is on
   * the API host (no ticket, no pass). */
  ttyEndpoint?: () => Promise<string | null>;
  /** Mint a ticket and redeem it at `ttyEndpoint` for the pass cookie. */
  redeemPass?: (ttyEndpoint: string) => Promise<void>;
  onOpen: () => void;
  onData: (bytes: Uint8Array) => void;
  onState: (state: ConnectionState) => void;
  createSocket?: (url: string) => SocketLike;
  /** Backoff before reconnect attempt `n` (1-based). */
  backoffMs?: (attempt: number) => number;
  /** Failed dials in a row, with no attach, before `unreachable`. */
  maxAttempts?: number;
  pingMs?: number;
}

const OPEN = 1;
const DEFAULT_PING_MS = 30_000;
/** About 35 s of backoff, plus the broker's 10 s agent wait per dial. */
export const DEFAULT_MAX_ATTEMPTS = 6;

function defaultBackoff(attempt: number): number {
  return Math.min(1_000 * 2 ** (attempt - 1), 10_000);
}

interface TtyTicket {
  ticket: string;
  expires_in: number;
}

/**
 * The ticket→pass exchange: `POST …/workspace/tty-ticket` on the API host,
 * then `POST …/workspace/tty-pass` `{ticket}` at `ttyEndpoint`, which answers
 * 204 and sets the HttpOnly pass cookie. Throws when either step fails.
 */
export async function redeemTtyPass(slug: string, ttyEndpoint: string): Promise<void> {
  const { ticket } = await apiFetch<TtyTicket>(`${workspacePath(slug)}/tty-ticket`, {
    method: "POST",
  });
  const res = await fetch(new URL(`${workspacePath(slug)}/tty-pass`, ttyEndpoint).href, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ticket }),
  });
  if (!res.ok) throw new Error(`the terminal pass was refused (${res.status})`);
}

export class TerminalConnection {
  private readonly options: TerminalConnectionOptions;
  private socket: SocketLike | null = null;
  private size: { cols: number; rows: number } | null = null;
  private attempt = 0;
  private authRetried = false;
  /** Whether the current socket has delivered its first frame (the attach). */
  private opened = false;
  private disposed = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(options: TerminalConnectionOptions) {
    this.options = options;
  }

  /** Open (or re-open, after `superseded`) the connection. */
  connect(): void {
    this.attempt = 0;
    this.authRetried = false;
    this.options.onState({ kind: "connecting" });
    void this.dial();
  }

  /** Raw keyboard input — the only frames that touch the session's idle clock. */
  write(bytes: Uint8Array): void {
    if (this.socket?.readyState === OPEN) this.socket.send(bytes);
  }

  resize(cols: number, rows: number): void {
    this.size = { cols, rows };
    this.sendResize();
  }

  dispose(): void {
    this.disposed = true;
    this.clearTimers();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onclose = null;
      socket.close(1000);
    }
  }

  private async dial(): Promise<void> {
    let url: string;
    let token: string | null;
    try {
      [url, token] = await Promise.all([this.options.url(), this.options.token()]);
    } catch {
      this.scheduleRetry();
      return;
    }
    if (this.disposed) return;
    if (token === null) {
      this.options.onState({ kind: "ended", code: CLOSE_NOT_LIVE });
      return;
    }
    try {
      const ttyEndpoint = (await this.options.ttyEndpoint?.()) ?? null;
      if (ttyEndpoint !== null && this.options.redeemPass) {
        await this.options.redeemPass(ttyEndpoint);
      }
    } catch {
      this.scheduleRetry();
      return;
    }
    if (this.disposed) return;
    const socket = (this.options.createSocket ?? ((u) => new WebSocket(u)))(url);
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    socket.onopen = () => {
      socket.send(JSON.stringify({ token }));
      this.sendResize();
      this.startPing();
    };
    socket.onmessage = (event) => this.receive(event.data);
    socket.onclose = (event) => this.closed(socket, event.code);
  }

  private receive(data: unknown): void {
    if (!this.opened) {
      // The broker sends nothing before it accepts the token, so the first
      // frame marks the attach: the agent's ring replay starts here.
      this.opened = true;
      this.attempt = 0;
      this.authRetried = false;
      this.options.onOpen();
      this.options.onState({ kind: "open" });
    }
    // With `binaryType = "arraybuffer"` every non-text frame is PTY bytes. Text
    // frames are control; the only one the broker sends us is `pong`.
    if (typeof data !== "string") {
      this.options.onData(new Uint8Array(data as ArrayBuffer));
    }
  }

  private closed(socket: SocketLike, code: number): void {
    if (socket !== this.socket) return;
    this.socket = null;
    this.opened = false;
    this.clearTimers();
    if (this.disposed) return;
    if (code === CLOSE_SUPERSEDED) {
      this.options.onState({ kind: "superseded" });
    } else if (code === CLOSE_NOT_LIVE || code === CLOSE_REVOKED) {
      this.options.onState({ kind: "ended", code });
    } else if (code === CLOSE_AUTH) {
      if (this.authRetried) {
        this.options.onState({ kind: "denied" });
      } else {
        this.authRetried = true;
        void this.dial();
      }
    } else {
      this.scheduleRetry();
    }
  }

  private scheduleRetry(): void {
    if (this.disposed) return;
    if (this.attempt >= (this.options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS)) {
      this.options.onState({ kind: "unreachable" });
      return;
    }
    this.attempt += 1;
    this.options.onState({ kind: "reconnecting", attempt: this.attempt });
    const delay = (this.options.backoffMs ?? defaultBackoff)(this.attempt);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.dial();
    }, delay);
  }

  private sendResize(): void {
    if (this.size && this.socket?.readyState === OPEN) {
      this.socket.send(JSON.stringify({ type: "resize", ...this.size }));
    }
  }

  private startPing(): void {
    this.pingTimer = setInterval(() => {
      if (this.socket?.readyState === OPEN) this.socket.send(JSON.stringify({ type: "ping" }));
    }, this.options.pingMs ?? DEFAULT_PING_MS);
  }

  private clearTimers(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.retryTimer = null;
    this.pingTimer = null;
  }
}
